// Partners — list + create + edit + secret-reveal modal + deep-link builder.
// Task 10 of Slice E (partners JWT integration).

import { TRANSFERS_UI_ENABLED } from '@/lib/features';
import { useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { DataTable, type DataTableColumn, DetailTabs, type DetailTab } from '@drivecars/admin-ui';
import { PageHeader, ToolBtn } from '@drivecars/admin-ui';
import {
  createPartner,
  fetchAdminPartnersList,
  fetchAdminPartnerStats,
  rotatePartnerSecret,
  setPartnerStatus,
  updatePartner,
  fetchAdminPartnerCredentials,
  createAdminPartnerCredential,
  retireAdminPartnerCredential,
  revokeAdminPartnerCredential,
  listPartnerMembers,
  invitePartnerOwner,
  type AdminPartner,
  type PartnerWriteInput,
  type AdminPartnerCredential,
} from '@/lib/admin-api';
import { api, ApiError } from '@/lib/api';
import { usePager } from '@/lib/use-pager';
import {
  buildEmbedSnippet,
  PARTNER_AUTH_PROVIDERS,
  PARTNER_PRODUCTS,
  PARTNER_SCOPES,
  type PartnerAuthProvider,
  type PartnerProduct,
  type PartnerScope,
} from '@drivecars/shared';
import { defaultProductOptions, nextProductState } from './partner-products-state';
import {
  authSaveBlockReason,
  nextAuthState,
  secretForPayload,
  type SecretIntent,
} from './partner-auth-state';
import { PartnerLocationsFieldset } from './PartnerLocationsFieldset';
import { PartnerVendorsFieldset } from './PartnerVendorsFieldset';
import { PartnerCommercialModelFieldset } from './PartnerCommercialModelFieldset';
import { withOffModelRatesCleared } from './partner-commercial-model-state';
import { PartnerInvoicesPanel } from './PartnerInvoicesPanel';

// DriveCars selling to itself — the storefront and the app (migration 0220).
// Those rows are activated everywhere published by construction; the API
// refuses to narrow them (409 `first_party_everywhere`). Matched on the flag
// OR the slug, the same pair the API's guard uses, so the two cannot disagree
// about which rows are ours.
const FIRST_PARTY_SLUGS = new Set(['drivecars-website-partner', 'drivecars-mobile-partner']);

function isDriveCarsPartner(partner: AdminPartner): boolean {
  return partner.is_first_party || FIRST_PARTY_SLUGS.has(partner.slug);
}

// Public hosts for the affiliate link + widget embed snippet shown to admins.
const AFFILIATE_HOST = 'https://drivecars.ai';
// The loader host used to be hard-coded to a widget.drivecars.ai host that
// did not resolve. VITE_SDK_LOADER_URL is now set on the backoffice Vercel
// project by terraform (environments/production/main.tf,
// module.rewrite_vercel_backoffice) from var.sdk_domain; the literal below is
// the same value and only covers a local `pnpm dev` with no .env.
const WIDGET_LOADER = import.meta.env.VITE_SDK_LOADER_URL ?? 'https://sdk.drivecars.ai/v1/loader.js';
const WIDGET_API_BASE = 'https://api.drivecars.ai';

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString();

// Lowercase — the widget loader's tag->bundle map is keyed lowercase, and the
// API's enabled_components zod schema (apps/api/src/modules/admin/partners.ts)
// only accepts these exact lowercase values.
//
// WIDGETS, not products. This list and PARTNER_PRODUCTS are two different axes
// that read as interchangeable, and conflating them is the mistake this whole
// slice exists to prevent — so they stay separate constants and separate
// fieldsets in the editor below, each labelled for what it actually controls.
const ALL_COMPONENTS = ['search', 'cart', 'checkout', 'payment'] as const;

// ─── Status pill ──────────────────────────────────────────────────────

function StatusPill({ status }: { status: AdminPartner['status'] }) {
  const map: Record<AdminPartner['status'], { bg: string; text: string; label: string }> = {
    active: { bg: '#dcfce7', text: '#166534', label: 'Active' },
    suspended: { bg: '#fef9c3', text: '#854d0e', label: 'Suspended' },
    revoked: { bg: '#fee2e2', text: '#991b1b', label: 'Revoked' },
  };
  const { bg, text, label } = map[status] ?? map.active;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ backgroundColor: bg, color: text }}
    >
      {label}
    </span>
  );
}

// ─── Copy button ──────────────────────────────────────────────────────

function CopyBtn({ value, title = 'Copy' }: { value: string; title?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      type="button"
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        handleCopy();
      }}
      className="ml-1.5 inline-flex items-center rounded px-1 py-0.5 text-[11px] text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)] transition"
    >
      {copied ? '✓' : '⧉'}
    </button>
  );
}

// ─── Table columns ────────────────────────────────────────────────────

const buildColumns = (
  onEdit: (p: AdminPartner) => void,
): DataTableColumn<AdminPartner>[] => [
  {
    key: 'name',
    h: 'Name',
    cell: (r) => (
      <button
        type="button"
        className="text-left font-semibold text-[color:var(--bo-ink)] hover:underline"
        onClick={() => onEdit(r)}
      >
        {r.name}
      </button>
    ),
  },
  {
    key: 'slug',
    h: 'Slug',
    mono: true,
    cell: (r) => <span className="text-[12.5px]">{r.slug}</span>,
  },
  {
    key: 'status',
    h: 'Status',
    cell: (r) => <StatusPill status={r.status} />,
  },
  {
    key: 'mor_mode',
    h: 'MoR mode',
    cell: (r) => (
      <span className="text-[12.5px]">
        {r.mor_mode === 'drivecars' ? 'DriveCars' : 'Partner invoiced'}
      </span>
    ),
  },
  {
    key: 'booking_mode',
    h: 'Booking mode',
    cell: (r) => (
      <span className="text-[12.5px]">
        {r.booking_mode === 'fixed' ? 'Fixed' : 'Variable'}
      </span>
    ),
  },
  {
    key: 'secret_key_prefix',
    h: 'Key prefix',
    mono: true,
    cell: (r) => (
      <span className="flex items-center text-[12px]">
        {r.secret_key_prefix}…
        <CopyBtn value={r.secret_key_prefix} title="Copy key prefix" />
      </span>
    ),
  },
  {
    key: 'created_at',
    h: 'Created',
    cell: (r) => <span className="tabular-nums text-[color:var(--bo-mute)]">{fmtDate(r.created_at)}</span>,
  },
];

// ─── Empty draft ──────────────────────────────────────────────────────

const EMPTY_DRAFT: PartnerWriteInput = {
  slug: '',
  name: '',
  origin_allowlist: [],
  mor_mode: 'drivecars',
  // Matching 0225's column DEFAULT. A new partner is variable-price until
  // ops deliberately flips it to fixed — the crew fixed-price opt-in flow
  // wired to it (Task 5) has nothing to point at until this is chosen.
  booking_mode: 'variable',
  commission_pct: null,
  commission_flat: null,
  platform_pct_override: null,
  pickup_buffer_override: null,
  // Matching 0241's column DEFAULTs. A new partner earns us the booking fee
  // with no share rule set — which leaves the legacy commission fields above
  // in force, exactly as every partner created before 0241 did. Anything else
  // is a contract ops signs on purpose.
  commercial_model: 'booking_fee',
  fee_share_pct: null,
  markup_pct: null,
  markup_share_pct: null,
  supplier_commission_share_pct: null,
  // 0241 backfills sdk_mor from mor_mode, and EMPTY_DRAFT's mor_mode is
  // 'drivecars' — so the two agree for a new partner without the form having
  // to keep them in step.
  sdk_mor: 'drivecars',
  billing_cycle_days: 14,
  theme_overrides: {},
  enabled_components: ['search', 'cart', 'checkout', 'payment'],
  // Rentals only, matching the column DEFAULT in migration 0215 and
  // CREATE_DEFAULT_PRODUCTS in apps/api/src/modules/admin/partners.ts.
  // Deliberately narrower than the components default above: transfers is
  // something ops sells on purpose, and nobody should acquire it by leaving a
  // new-partner form alone.
  enabled_products: ['rentals'],
  default_product: null,
  // Matching 0219's column DEFAULT. A new partner uses OUR auth until someone
  // deliberately says otherwise — partner auth needs an endpoint they have to
  // build, and defaulting to it would create partners nobody can sign into.
  auth_provider: 'drivecars',
  auth_introspection_url: null,
  // auth_introspection_secret is deliberately ABSENT rather than null: absent
  // means "no opinion" to the API, and a create that names no secret should
  // create a partner with no secret, not one that had to be told so.
};

// ─── Auth provider labels ─────────────────────────────────────────────
//
// Whose auth signs the shopper in. Spelled out rather than shown as the raw
// enum, because 'drivecars' vs 'partner' read as a category of partner rather
// than as a choice about authentication.
const AUTH_PROVIDER_LABEL: Record<PartnerAuthProvider, string> = {
  drivecars: 'DriveCars signs their shoppers in',
  partner: 'They sign their own users in',
};

// ─── Secret-revealed modal ────────────────────────────────────────────

interface SecretRevealedModalProps {
  secret: string;
  slug: string;
  onDismiss: () => void;
}

function SecretRevealedModal({ secret, slug, onDismiss }: SecretRevealedModalProps) {
  const snippet = `// your-app/api/drivecars-session.ts
const r = await fetch('https://api.drivecars.ai/partners/sessions', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Origin: 'https://yoursite.com' },
  body: JSON.stringify({ slug: '${slug}', secretKey: process.env.DRIVECARS_SECRET_KEY }),
});
const { token } = await r.json();`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Secret revealed"
      className="fixed inset-0 z-[60] grid place-items-center bg-black/60 px-3"
      // intentionally no click-outside-close — deliberate friction
    >
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 rounded-lg border border-[#fef08a] bg-[#fefce8] px-4 py-3">
          <p className="text-[13px] font-semibold text-[#854d0e]">
            ⚠ This is the ONLY time this secret will be shown. Copy it now and store it somewhere safe.
          </p>
        </div>

        <p className="mb-2 text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
          Secret key
        </p>
        <div className="mb-1 flex items-center gap-2 rounded-lg border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] px-3 py-2 font-mono text-[13px] break-all">
          <span className="flex-1 select-all">{secret}</span>
          <CopyBtn value={secret} title="Copy secret" />
        </div>

        <p className="mb-2 mt-4 text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
          Integration snippet
        </p>
        <pre className="mb-5 overflow-x-auto rounded-lg border border-[color:var(--bo-line)] bg-[#1e1e1e] px-3 py-3 text-[11.5px] leading-relaxed text-[#d4d4d4]">
          {snippet}
        </pre>

        <button
          type="button"
          onClick={onDismiss}
          className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-[color:var(--bo-ink)] text-[12.5px] font-semibold text-white transition hover:bg-black"
        >
          I&apos;ve copied it, dismiss
        </button>
      </div>
    </div>
  );
}

// ─── Deep-link URL builder ────────────────────────────────────────────

function DeepLinkBuilder({ slug }: { slug: string }) {
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const url = (() => {
    const base = 'https://drivecars.ai/cars';
    const sp = new URLSearchParams();
    sp.set('ref', slug);
    if (country) sp.set('country', country);
    if (city) sp.set('city', city);
    if (dateFrom) sp.set('dateFrom', dateFrom);
    if (dateTo) sp.set('dateTo', dateTo);
    return `${base}?${sp.toString()}`;
  })();

  return (
    <div className="mt-6 rounded-xl border border-[color:var(--bo-line)] p-4">
      <p className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
        Deep-link URL builder
      </p>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-[color:var(--bo-mute)]">Country slug</span>
          <input
            type="text"
            placeholder="eg. france"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="mt-1 h-9 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13px] outline-none focus:border-[color:var(--bo-accent)]"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-[color:var(--bo-mute)]">City slug</span>
          <input
            type="text"
            placeholder="eg. paris"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="mt-1 h-9 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13px] outline-none focus:border-[color:var(--bo-accent)]"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-[color:var(--bo-mute)]">Date from</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 h-9 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13px] outline-none focus:border-[color:var(--bo-accent)]"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-[color:var(--bo-mute)]">Date to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 h-9 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13px] outline-none focus:border-[color:var(--bo-accent)]"
          />
        </label>
      </div>
      <div className="mt-3">
        <span className="text-[11px] uppercase tracking-wide text-[color:var(--bo-mute)]">Generated URL</span>
        <div className="mt-1 flex items-center gap-2 rounded-lg border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] px-3 py-2 font-mono text-[11.5px] break-all">
          <span className="flex-1 select-all">{url}</span>
          <CopyBtn value={url} title="Copy URL" />
        </div>
      </div>
    </div>
  );
}

// ─── Partner insights: affiliate link + widget snippet + stats ────────

function PartnerInsights({ partner }: { partner: AdminPartner }) {
  const components = partner.enabled_components?.length
    ? partner.enabled_components
    : ['search'];
  const affiliateLink = `${AFFILIATE_HOST}/?ref=${partner.slug}`;
  const snippet = buildEmbedSnippet({
    loaderUrl: WIDGET_LOADER,
    apiBase: WIDGET_API_BASE,
    components,
  });

  const { data: stats } = useQuery({
    queryKey: ['admin', 'partner-stats', partner.id],
    queryFn: ({ signal }) => fetchAdminPartnerStats(partner.id, signal),
  });

  return (
    <div className="mt-6 rounded-xl border border-[color:var(--bo-line)] p-4">
      <p className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
        Affiliate, widget &amp; performance
      </p>

      <div className="grid grid-cols-3 gap-3">
        {[
          ['Bookings (cycle)', stats ? String(stats.bookingsThisCycle) : '—'],
          ['Commission (cycle)', stats ? `€${stats.commissionThisCycle.toFixed(2)}` : '—'],
          ['Bookings (all-time)', stats ? String(stats.bookingsAllTime) : '—'],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] p-3">
            <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bo-mute)]">{label}</div>
            <div className="mt-0.5 text-[18px] font-semibold tabular-nums">{value}</div>
          </div>
        ))}
      </div>

      <div className="mt-3">
        <span className="text-[11px] uppercase tracking-wide text-[color:var(--bo-mute)]">Affiliate link</span>
        <div className="mt-1 flex items-center gap-2 rounded-lg border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] px-3 py-2 font-mono text-[11.5px] break-all">
          <span className="flex-1 select-all">{affiliateLink}</span>
          <CopyBtn value={affiliateLink} title="Copy link" />
        </div>
      </div>

      <div className="mt-3">
        <span className="text-[11px] uppercase tracking-wide text-[color:var(--bo-mute)]">Widget embed snippet</span>
        <div className="mt-1 flex items-start gap-2 rounded-lg border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] px-3 py-2 font-mono text-[11.5px]">
          <pre className="flex-1 overflow-x-auto whitespace-pre">{snippet}</pre>
          <CopyBtn value={snippet} title="Copy snippet" />
        </div>
      </div>
    </div>
  );
}

// ─── Credential status pill ────────────────────────────────────────────

function CredentialStatusPill({ status }: { status: AdminPartnerCredential['status'] }) {
  const map: Record<AdminPartnerCredential['status'], { bg: string; text: string; label: string }> = {
    active: { bg: '#dcfce7', text: '#166534', label: 'Active' },
    retiring: { bg: '#fef9c3', text: '#854d0e', label: 'Retiring' },
    revoked: { bg: '#fee2e2', text: '#991b1b', label: 'Revoked' },
  };
  const { bg, text, label } = map[status] ?? map.active;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ backgroundColor: bg, color: text }}
    >
      {label}
    </span>
  );
}

// ─── Credentials panel (Credentials tab) ───────────────────────────────
//
// OAuth client-credential pairs for the /v1 partner API. Separate from
// PartnerInsights' widget affiliate/embed material above — these mint the
// client_id/client_secret pair a partner's OWN SERVER exchanges at
// POST /oauth/token, not a browser-facing widget secret. Reuses
// SecretRevealedModal (below the editor) for the one-time-secret reveal —
// no second modal.

interface PartnerCredentialsPanelProps {
  partnerId: number;
  onSecretRevealed: (secret: string) => void;
}

function PartnerCredentialsPanel({ partnerId, onSecretRevealed }: PartnerCredentialsPanelProps) {
  const queryClient = useQueryClient();
  const queryKey = ['admin', 'partner-credentials', partnerId];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchAdminPartnerCredentials(partnerId, signal),
  });
  const credentials = data?.credentials ?? [];

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey });
  };

  const [showCreate, setShowCreate] = useState(false);
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<PartnerScope[]>([]);
  const [confirmRetire, setConfirmRetire] = useState<number | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<number | null>(null);

  const createMut = useMutation({
    mutationFn: () => createAdminPartnerCredential(partnerId, { label, scopes }),
    onSuccess: (res) => {
      invalidate();
      setShowCreate(false);
      setLabel('');
      setScopes([]);
      onSecretRevealed(res.oneTimeSecret);
    },
  });

  const retireMut = useMutation({
    mutationFn: (credId: number) => retireAdminPartnerCredential(partnerId, credId),
    onSuccess: () => {
      invalidate();
      setConfirmRetire(null);
    },
  });

  const revokeMut = useMutation({
    mutationFn: (credId: number) => revokeAdminPartnerCredential(partnerId, credId),
    onSuccess: () => {
      invalidate();
      setConfirmRevoke(null);
    },
  });

  const mutationError = createMut.error ?? retireMut.error ?? revokeMut.error;
  // createCredential (partners/credentials.ts) refuses a first-party partner
  // row with a 422 + { error: 'first_party_partner_cannot_hold_credentials' }
  // (admin/partners.ts) — surfaced here as a specific, readable sentence
  // rather than the generic "Action failed: ..." every other mutation error
  // falls back to, since this one is an expected business rule an admin can
  // hit by simply trying, not a bug.
  const mutationErrorMessage = (() => {
    if (!mutationError) return null;
    if (mutationError instanceof ApiError) {
      const body = mutationError.body as { error?: string } | undefined;
      if (body?.error === 'first_party_partner_cannot_hold_credentials') {
        return 'This partner is a first-party DriveCars record (the storefront widget itself) and cannot hold API credentials.';
      }
    }
    return (mutationError as Error).message;
  })();

  const toggleScope = (scope: PartnerScope) => {
    setScopes((cur) => (cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope]));
  };

  const fmtDateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-[color:var(--bo-mute)]">
          OAuth client credentials for this partner&apos;s server-to-server /v1 API integration.
        </p>
        {!showCreate && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex h-9 shrink-0 items-center rounded-lg bg-[color:var(--bo-ink)] px-3 text-[12px] font-semibold text-white transition hover:bg-black"
          >
            New credential
          </button>
        )}
      </div>

      {mutationErrorMessage ? (
        <div role="alert" className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          {mutationErrorMessage}
        </div>
      ) : null}

      {error ? (
        <div role="alert" className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          Couldn&apos;t load credentials: {(error as Error).message}
        </div>
      ) : null}

      {showCreate && (
        <div className="rounded-xl border border-[color:var(--bo-line)] p-4">
          <p className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
            New credential
          </p>
          <label className="block">
            <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              Label
            </span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Production"
              className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
            />
          </label>
          <fieldset className="mt-3">
            <legend className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              Scopes
            </legend>
            <div className="mt-1.5 flex flex-wrap gap-3">
              {PARTNER_SCOPES.map((scope) => (
                <label key={scope} className="flex items-center gap-1.5 text-[12.5px]">
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope)}
                    onChange={() => toggleScope(scope)}
                  />
                  <span className="font-mono">{scope}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowCreate(false);
                setLabel('');
                setScopes([]);
              }}
              className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12px] font-medium text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => createMut.mutate()}
              disabled={createMut.isPending || !label.trim() || scopes.length === 0}
              className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3 text-[12px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {createMut.isPending ? 'Creating…' : 'Create credential'}
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-[color:var(--bo-line)]">
        <table className="w-full text-left text-[12.5px]">
          <thead className="bg-[color:var(--bo-bg)] text-[11px] uppercase tracking-wide text-[color:var(--bo-mute)]">
            <tr>
              <th className="px-3 py-2 font-semibold">Label</th>
              <th className="px-3 py-2 font-semibold">Client ID</th>
              <th className="px-3 py-2 font-semibold">Scopes</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold">Last used</th>
              <th className="px-3 py-2 font-semibold" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-[color:var(--bo-mute)]">
                  Loading…
                </td>
              </tr>
            ) : credentials.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-[color:var(--bo-mute)]">
                  No credentials yet.
                </td>
              </tr>
            ) : (
              credentials.map((cred) => (
                <tr key={cred.id} className="border-t border-[color:var(--bo-line)]">
                  <td className="px-3 py-2 font-medium text-[color:var(--bo-ink)]">{cred.label}</td>
                  <td className="px-3 py-2 font-mono text-[11.5px]">
                    <span className="flex items-center">
                      {cred.client_id}
                      <CopyBtn value={cred.client_id} title="Copy client ID" />
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {cred.scopes.map((s) => (
                        <span
                          key={s}
                          className="rounded bg-[color:var(--bo-bg)] px-1.5 py-0.5 font-mono text-[10.5px] text-[color:var(--bo-mute)]"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <CredentialStatusPill status={cred.status} />
                    {cred.status === 'retiring' && cred.retire_after ? (
                      <span className="mt-0.5 block text-[10.5px] text-[color:var(--bo-mute)]">
                        until {fmtDateTime(cred.retire_after)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-[color:var(--bo-mute)]">{fmtDateTime(cred.last_used_at)}</td>
                  <td className="px-3 py-2">
                    {cred.status === 'active' && (
                      <div className="flex items-center justify-end gap-1.5">
                        {confirmRetire === cred.id ? (
                          <>
                            <span className="text-[11px] text-[#854d0e]">Retire with a 24h overlap?</span>
                            <button
                              type="button"
                              onClick={() => retireMut.mutate(cred.id)}
                              disabled={retireMut.isPending}
                              className="text-[11.5px] font-semibold text-[#854d0e] underline hover:no-underline"
                            >
                              Confirm
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmRetire(null)}
                              className="text-[11.5px] text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmRetire(cred.id)}
                            disabled={retireMut.isPending || revokeMut.isPending}
                            className="rounded-lg border border-[#d97706] px-2.5 py-1 text-[11.5px] font-medium text-[#d97706] hover:bg-[#fffbeb] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            Retire
                          </button>
                        )}
                      </div>
                    )}
                    {(cred.status === 'active' || cred.status === 'retiring') && (
                      <div className="mt-1 flex items-center justify-end gap-1.5">
                        {confirmRevoke === cred.id ? (
                          <>
                            <span className="text-[11px] text-[#9f1239]">Revoke immediately?</span>
                            <button
                              type="button"
                              onClick={() => revokeMut.mutate(cred.id)}
                              disabled={revokeMut.isPending}
                              className="text-[11.5px] font-semibold text-[#9f1239] underline hover:no-underline"
                            >
                              Confirm
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmRevoke(null)}
                              className="text-[11.5px] text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmRevoke(cred.id)}
                            disabled={retireMut.isPending || revokeMut.isPending}
                            className="rounded-lg border border-[#ef4444] px-2.5 py-1 text-[11.5px] font-medium text-[#ef4444] hover:bg-[#fff1f2] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Team panel (Team tab) ─────────────────────────────────────────────
//
// Who at the partner can sign in to the partner console
// (partners.drivecars.ai). A partner's owners invite everyone else
// themselves, so what ops does here is seed the FIRST owner — a brand-new
// partner has nobody who can let anyone in. After that this is a read-only
// answer to "who at Acme can see this?".
//
// Not rendered at all for a first-party partner (see the tabs below): that
// row is the DriveCars storefront itself and has no console, and the API
// refuses the invite with a 422 either way.
//
// Exported for its test, which renders it without the whole editor around it.

interface PartnerTeamPanelProps {
  partnerId: number;
}

export function PartnerTeamPanel({ partnerId }: PartnerTeamPanelProps) {
  const queryClient = useQueryClient();
  const queryKey = ['admin', 'partner-members', partnerId];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: ({ signal }) => listPartnerMembers(partnerId, signal),
  });
  const members = data?.members ?? [];
  const invites = data?.invites ?? [];

  const [email, setEmail] = useState('');
  const [invited, setInvited] = useState<{ email: string; emailSent: boolean } | null>(null);

  const inviteMut = useMutation({
    mutationFn: () => invitePartnerOwner(partnerId, email.trim()),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey });
      setInvited({ email: res.invite.email, emailSent: res.emailSent });
      setEmail('');
    },
  });

  // Expected refusals get a sentence; anything else falls back to the
  // generic message, same as the credentials panel above.
  const inviteErrorMessage = (() => {
    const err = inviteMut.error;
    if (!err) return null;
    if (err instanceof ApiError) {
      const body = err.body as { error?: string } | undefined;
      if (body?.error === 'already_a_member') return 'That person is already on this partner’s team.';
      if (body?.error === 'first_party_partner') {
        return 'This partner is a first-party DriveCars record (the storefront widget itself) and has no console team.';
      }
      if (body?.error === 'partner_revoked') return 'This partner has been revoked and cannot take new team members.';
      if (body?.error === 'invalid_payload') return 'Enter a valid email address.';
    }
    return (err as Error).message;
  })();

  const fmtDateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
  const canSubmit = /^\S+@\S+\.\S+$/.test(email.trim()) && !inviteMut.isPending;

  return (
    <div className="space-y-4">
      <p className="text-[12px] text-[color:var(--bo-mute)]">
        People who can sign in to the partner console for this partner. Owners manage their own team —
        invite the first owner here.
      </p>

      <form
        className="rounded-xl border border-[color:var(--bo-line)] p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          setInvited(null);
          inviteMut.mutate();
        }}
      >
        <p className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
          Invite owner
        </p>
        <div className="flex items-end gap-2">
          <label className="block flex-1">
            <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@partner.com"
              className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
            />
          </label>
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex h-10 shrink-0 items-center rounded-lg bg-[color:var(--bo-ink)] px-3 text-[12px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {inviteMut.isPending ? 'Inviting…' : 'Invite owner'}
          </button>
        </div>
        <p className="mt-2 text-[11.5px] text-[color:var(--bo-mute)]">
          They get an email with a link to set a password. The link expires after 14 days; inviting the same
          address again replaces it.
        </p>
      </form>

      {inviteErrorMessage ? (
        <div role="alert" className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          {inviteErrorMessage}
        </div>
      ) : null}

      {invited ? (
        invited.emailSent ? (
          <div role="status" className="rounded-lg border border-[#bbf7d0] bg-[#f0fdf4] px-3 py-2 text-[12.5px] text-[#166534]">
            Invitation sent to {invited.email}.
          </div>
        ) : (
          <div role="alert" className="rounded-lg border border-[#fde68a] bg-[#fffbeb] px-3 py-2 text-[12.5px] text-[#854d0e]">
            The invitation for {invited.email} was recorded, but the email could not be sent. Invite them again to
            resend it.
          </div>
        )
      ) : null}

      {error ? (
        <div role="alert" className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          Couldn&apos;t load the team: {(error as Error).message}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-[color:var(--bo-line)]">
        <table className="w-full text-left text-[12.5px]">
          <thead className="bg-[color:var(--bo-bg)] text-[11px] uppercase tracking-wide text-[color:var(--bo-mute)]">
            <tr>
              <th className="px-3 py-2 font-semibold">Member</th>
              <th className="px-3 py-2 font-semibold">Role</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold">Joined</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-[color:var(--bo-mute)]">
                  Loading…
                </td>
              </tr>
            ) : members.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-[color:var(--bo-mute)]">
                  Nobody can sign in for this partner yet.
                </td>
              </tr>
            ) : (
              members.map((m) => (
                <tr key={m.partnerUserId} className="border-t border-[color:var(--bo-line)]">
                  <td className="px-3 py-2">
                    <span className="font-medium text-[color:var(--bo-ink)]">{m.email}</span>
                    {m.name ? (
                      <span className="mt-0.5 block text-[10.5px] text-[color:var(--bo-mute)]">{m.name}</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 capitalize">{m.role}</td>
                  <td className="px-3 py-2 capitalize text-[color:var(--bo-mute)]">{m.status}</td>
                  <td className="px-3 py-2 text-[color:var(--bo-mute)]">{fmtDateTime(m.joinedAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {invites.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[color:var(--bo-line)]">
          <table className="w-full text-left text-[12.5px]">
            <thead className="bg-[color:var(--bo-bg)] text-[11px] uppercase tracking-wide text-[color:var(--bo-mute)]">
              <tr>
                <th className="px-3 py-2 font-semibold">Pending invitation</th>
                <th className="px-3 py-2 font-semibold">Role</th>
                <th className="px-3 py-2 font-semibold">Sent</th>
                <th className="px-3 py-2 font-semibold">Expires</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((i) => (
                <tr key={i.id} className="border-t border-[color:var(--bo-line)]">
                  <td className="px-3 py-2 font-medium text-[color:var(--bo-ink)]">{i.email}</td>
                  <td className="px-3 py-2 capitalize">{i.role}</td>
                  <td className="px-3 py-2 text-[color:var(--bo-mute)]">{fmtDateTime(i.invitedAt)}</td>
                  <td className="px-3 py-2 text-[color:var(--bo-mute)]">{fmtDateTime(i.expiresAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Partner editor modal ─────────────────────────────────────────────

interface EditorProps {
  mode: 'create' | 'edit';
  partner?: AdminPartner;
  draft: PartnerWriteInput;
  busy: boolean;
  onClose: () => void;
  onSave: (payload: PartnerWriteInput) => void;
  onRotateSecret: () => void;
  onSetStatus: (action: 'suspend' | 'activate' | 'revoke') => void;
  statusBusy: boolean;
  rotateBusy: boolean;
  /** Reveals a freshly-minted API credential's one-time secret — reuses the
   *  SAME SecretRevealedModal the widget secret create/rotate flow already
   *  shows (rendered by the page, not the editor, so it survives the editor
   *  closing). */
  onCredentialSecretRevealed: (secret: string) => void;
}

function PartnerEditor({
  mode,
  partner,
  draft: initial,
  busy,
  onClose,
  onSave,
  onRotateSecret,
  onSetStatus,
  statusBusy,
  rotateBusy,
  onCredentialSecretRevealed,
}: EditorProps) {
  const [draft, setDraft] = useState<PartnerWriteInput>(initial);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [originsText, setOriginsText] = useState(
    () => initial.origin_allowlist.join('\n'),
  );
  const [themeJson, setThemeJson] = useState(
    () => JSON.stringify(initial.theme_overrides ?? {}, null, 2),
  );
  const [themeJsonError, setThemeJsonError] = useState('');
  const [rotateConfirm, setRotateConfirm] = useState(false);
  // What this Save should do to the STORED introspection secret. Starts at
  // 'keep' on every open, which is what makes an unrelated edit — commission,
  // origins, status — incapable of disturbing a partner's configured bearer.
  const [secretIntent, setSecretIntent] = useState<SecretIntent>({ kind: 'keep' });

  // DriveCars' own storefront row. Edit-only by construction: a created
  // partner is never first-party (the API's create schema has no such field).
  const firstPartyLocked = partner?.is_first_party === true;

  const set = <K extends keyof PartnerWriteInput>(k: K, v: PartnerWriteInput[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const parseOrigins = (text: string): string[] =>
    text
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);

  const handleSave = () => {
    let parsedTheme: Record<string, unknown> = {};
    if (themeJson.trim()) {
      try {
        parsedTheme = JSON.parse(themeJson) as Record<string, unknown>;
      } catch {
        setThemeJsonError('Invalid JSON — fix before saving.');
        return;
      }
    }
    setThemeJsonError('');
    const secret = secretForPayload(secretIntent);
    onSave({
      // The commercial rates a model never reads are nulled at save, so a
      // model switch does not carry the previous model's rates onto the row
      // (partner-commercial-model-state.ts).
      ...withOffModelRatesCleared(draft),
      origin_allowlist: parseOrigins(originsText),
      theme_overrides: parsedTheme,
      // Spread-in rather than assigned, so that "leave the stored secret
      // alone" is an ABSENT key rather than a present-but-undefined one. Both
      // survive JSON.stringify identically today; only one of them survives
      // somebody later switching the transport, and the failure that change
      // would cause — every Save silently wiping the bearer — is invisible
      // until a partner's shoppers stop being able to sign in.
      ...(secret !== undefined ? { auth_introspection_secret: secret } : {}),
    });
  };

  const toggleComponent = (comp: string) => {
    const cur = draft.enabled_components ?? [];
    const next = cur.includes(comp) ? cur.filter((c) => c !== comp) : [...cur, comp];
    set('enabled_components', next);
  };

  // Products move both fields at once — unchecking one can strand the default —
  // so this goes through nextProductState rather than a `set()` per field.
  const productState = {
    enabled_products: draft.enabled_products ?? [],
    default_product: draft.default_product ?? null,
  };
  const toggleProduct = (product: PartnerProduct) => {
    const next = nextProductState(productState, { toggle: product });
    setDraft((d) => ({
      ...d,
      enabled_products: next.enabled_products,
      default_product: next.default_product,
    }));
  };

  // Auth moves as a pair for the same reason products do: provider='partner'
  // with no URL is refused by 0219's CHECK, and each field on its own looks
  // like what ops asked for.
  const authState = {
    auth_provider: draft.auth_provider ?? 'drivecars',
    auth_introspection_url: draft.auth_introspection_url ?? null,
  };
  const setAuthProvider = (provider: PartnerAuthProvider) => {
    const next = nextAuthState(authState, { provider });
    setDraft((d) => ({
      ...d,
      auth_provider: next.auth_provider,
      auth_introspection_url: next.auth_introspection_url,
    }));
  };
  const authBlockReason = authSaveBlockReason(authState);

  const currentStatus = partner?.status;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 overflow-y-auto bg-black/40 px-3 py-8"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="mx-auto w-full max-w-xl rounded-2xl bg-white p-5 shadow-xl">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[16px] font-semibold tracking-tight">
            {mode === 'create' ? 'New partner' : `Edit: ${partner?.name ?? ''}`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-[12px] text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]"
          >
            Close
          </button>
        </div>

        {(() => {
          // Details form content — identical in both modes. Wrapped in
          // DetailTabs alongside a Credentials tab ONLY in edit mode
          // (credentials belong to a partner id that doesn't exist yet in
          // create mode) — same "edit-mode-only, tab bar absent otherwise"
          // treatment as DeepLinkBuilder/PartnerInsights below already get.
          const detailsContent =
        <div className="space-y-3">
          {/* Slug */}
          <label className="block">
            <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              Slug
            </span>
            <input
              type="text"
              required
              value={draft.slug ?? ''}
              onChange={(e) => set('slug', e.target.value)}
              disabled={mode === 'edit'}
              placeholder="acme-travel"
              className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 font-mono text-[13.5px] outline-none focus:border-[color:var(--bo-accent)] disabled:bg-[color:var(--bo-bg)] disabled:text-[color:var(--bo-mute)]"
            />
            {mode === 'edit' && (
              <span className="mt-0.5 block text-[11px] text-[color:var(--bo-mute)]">
                Slug is immutable after creation.
              </span>
            )}
          </label>

          {/* Name */}
          <label className="block">
            <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              Name <span className="text-[#ef4444]">*</span>
            </span>
            <input
              type="text"
              required
              value={draft.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Acme Travel"
              className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
            />
          </label>

          {/* Origin allowlist */}
          <label className="block">
            <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              Origin allowlist
            </span>
            <textarea
              rows={3}
              value={originsText}
              onChange={(e) => setOriginsText(e.target.value)}
              placeholder={`https://example.com\n*.example.com`}
              className="mt-1 w-full rounded-lg border border-[color:var(--bo-line)] px-3 py-2 font-mono text-[12.5px] outline-none focus:border-[color:var(--bo-accent)]"
            />
            <span className="mt-0.5 block text-[11px] text-[color:var(--bo-mute)]">
              One per line. Supports{' '}
              <code className="font-mono">https://example.com</code> exact match or{' '}
              <code className="font-mono">*.example.com</code> subdomain wildcard.
            </span>
          </label>

          {/* MoR mode */}
          <fieldset>
            <legend className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              MoR mode
            </legend>
            <div className="mt-1.5 flex gap-4">
              <label className="flex items-center gap-1.5 text-[13px]">
                <input
                  type="radio"
                  name="mor_mode"
                  value="drivecars"
                  checked={draft.mor_mode === 'drivecars'}
                  onChange={() => set('mor_mode', 'drivecars')}
                />
                DriveCars merchant
              </label>
              <label className="flex items-center gap-1.5 text-[13px]">
                <input
                  type="radio"
                  name="mor_mode"
                  value="partner_invoiced"
                  checked={draft.mor_mode === 'partner_invoiced'}
                  onChange={() => set('mor_mode', 'partner_invoiced')}
                />
                Partner invoiced
              </label>
            </div>

            {/* THE SDK CHANNEL'S OWN ANSWER (0241).

                `mor_mode` above used to be the whole answer, which forced one
                partner to have one merchant of record on every surface it sold
                through. It cannot: the /v1 API is always partner-invoiced (the
                partner's own server took the booking and bills its own
                customer) and our storefront and app are always DriveCars.
                Only the partner's SDK embed is a genuine choice, and this is
                it.

                The copy is the control's whole point. Without it ops reads the
                radio as "merchant of record", flips it, and wonders why the
                /v1 bookings did not move. */}
            <div className="mt-2.5 flex gap-4">
              <label className="flex items-center gap-1.5 text-[13px]">
                <input
                  type="radio"
                  name="sdk_mor"
                  value="drivecars"
                  checked={(draft.sdk_mor ?? 'drivecars') === 'drivecars'}
                  onChange={() => set('sdk_mor', 'drivecars')}
                />
                SDK: DriveCars merchant
              </label>
              <label className="flex items-center gap-1.5 text-[13px]">
                <input
                  type="radio"
                  name="sdk_mor"
                  value="partner"
                  checked={draft.sdk_mor === 'partner'}
                  onChange={() => set('sdk_mor', 'partner')}
                />
                SDK: partner invoiced
              </label>
            </div>
            <p className="mt-1 text-[11.5px] text-[color:var(--bo-mute)]">
              /v1 API bookings are always partner-invoiced; the website and app are always
              DriveCars.
            </p>
          </fieldset>

          {/* Booking mode.

              THE STOREFRONT PARTNER STAYS VARIABLE. `is_first_party` is
              DriveCars selling to itself: our own storefront authenticates as
              that row on every request. Putting it in fixed mode is not a
              partner commitment anybody made — it narrows every CUSTOMER's
              grid to crews that opted in and stops a higher bid ever reaching
              the customer for consent. The API refuses it
              (`booking_mode_not_allowed`); the radio is disabled here so ops
              never meets that 400, with the reason stated rather than a dead
              control left unexplained. Variable stays selectable: the rule is
              about what the row may BECOME. */}
          <fieldset>
            <legend className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              Booking mode
            </legend>
            <div className="mt-1.5 flex flex-col gap-1.5">
              <label className="flex items-center gap-1.5 text-[13px]">
                <input
                  type="radio"
                  name="booking_mode"
                  value="variable"
                  checked={draft.booking_mode === 'variable'}
                  onChange={() => set('booking_mode', 'variable')}
                />
                Variable — crews may counter; the customer is told the final price is
                confirmed once the crew responds
              </label>
              <label
                className={`flex items-center gap-1.5 text-[13px] ${
                  firstPartyLocked ? 'text-[color:var(--bo-mute)]' : ''
                }`}
              >
                <input
                  type="radio"
                  name="booking_mode"
                  value="fixed"
                  checked={draft.booking_mode === 'fixed'}
                  disabled={firstPartyLocked}
                  onChange={() => set('booking_mode', 'fixed')}
                />
                Fixed — the price shown is the price paid; only crews that opted in to
                honour prices are shown
              </label>
              {firstPartyLocked ? (
                <p className="text-[12px] text-[color:var(--bo-mute)]">
                  The DriveCars storefront partner stays in variable mode — fixed pricing
                  is a promise a selling partner makes, and this row is our own shopfront.
                </p>
              ) : null}
            </div>
          </fieldset>

          {/* Commission */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                Commission % (e.g. 0.05)
              </span>
              <input
                type="number"
                step="0.001"
                min="0"
                value={draft.commission_pct ?? ''}
                onChange={(e) =>
                  set('commission_pct', e.target.value === '' ? null : Number(e.target.value))
                }
                placeholder="optional"
                className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </label>
            <label className="block">
              <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                Commission flat
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={draft.commission_flat ?? ''}
                onChange={(e) =>
                  set('commission_flat', e.target.value === '' ? null : Number(e.target.value))
                }
                placeholder="optional"
                className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </label>
          </div>

          {/* Billing cycle */}
          <label className="block">
            <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              Billing cycle (days)
            </span>
            <input
              type="number"
              min="1"
              value={draft.billing_cycle_days ?? 14}
              onChange={(e) => set('billing_cycle_days', Number(e.target.value))}
              className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
            />
          </label>

          {/* Billing cycle anchor — edit only. A new partner's anchor is the
              day it is created (the column default); ops moves it here when a
              contract names another date. Cleared input = leave it alone: the
              column is NOT NULL, so there is no "no anchor" to save. */}
          {mode === 'edit' && draft.billing_cycle_anchor !== undefined && (
            <label className="block">
              <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                Billing cycle starts on
              </span>
              <input
                type="date"
                value={draft.billing_cycle_anchor}
                onChange={(e) => {
                  if (e.target.value) set('billing_cycle_anchor', e.target.value);
                }}
                className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
              />
              <span className="mt-0.5 block text-[11px] text-[color:var(--bo-mute)]">
                Statements in the partner console are cut from this date (UTC). Changing it re-cuts past
                statements too.
              </span>
            </label>
          )}

          {/* HOW we earn on this partner (0241) — placed directly after the
              legacy commission fields on purpose: the note inside says which
              of the two Model 1 rules is in force, and that note is only
              useful next to the fields it is about. Unlike the cities and crews
              fieldsets further down, this one rides the ordinary Save — these
              are six plain columns on the draft, not a replace-set against a
              live fleet. */}
          <PartnerCommercialModelFieldset
            draft={draft}
            onChange={set}
            isDriveCarsPartner={firstPartyLocked}
          />

          {/* Products this partner sells — the COMMERCIAL axis. Kept visibly
              apart from the components fieldset below (its own legend, its own
              hint) because the two lists look alike and mean different things:
              this one gates the /v1 API, that one only decides which widgets
              their page may embed. */}
          <fieldset>
            <legend className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              Products this partner sells
            </legend>
            <p className="mt-1 text-[11.5px] text-[color:var(--bo-mute)]">
              What we sold them. Gates the API — narrowing this takes effect
              immediately, on tokens already issued.
            </p>
            <div className="mt-1.5 flex flex-wrap gap-3">
              {PARTNER_PRODUCTS.filter((product) => TRANSFERS_UI_ENABLED || product !== 'transfers' || productState.enabled_products.includes(product)).map((product) => {
                const checked = productState.enabled_products.includes(product);
                return (
                  <label key={product} className="flex items-center gap-1.5 text-[13px]">
                    <input
                      type="checkbox"
                      checked={checked}
                      // Unchecking the last product is refused by
                      // nextProductState, so say so rather than letting the
                      // click look broken.
                      disabled={checked && productState.enabled_products.length === 1}
                      onChange={() => toggleProduct(product)}
                    />
                    <span className="capitalize">{product}</span>
                  </label>
                );
              })}
            </div>
            <label className="mt-2.5 block">
              <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                Default product
              </span>
              <select
                value={draft.default_product ?? ''}
                onChange={(e) =>
                  set('default_product', e.target.value === '' ? null : e.target.value)
                }
                className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
              >
                {/* Empty string stands in for null — a <select> value cannot be
                    null, and null is what "no preference" must reach the API
                    as. Only enabled products are offered: a default outside
                    that list is refused by the DB CHECK. */}
                <option value="">No preference</option>
                {defaultProductOptions(productState).map((product) => (
                  <option key={product} value={product} className="capitalize">
                    {product}
                  </option>
                ))}
              </select>
            </label>
          </fieldset>

          {/* Activated cities — WHERE they may sell, after WHAT they may sell.
              Edit-only, and it saves itself: a partner has no id to activate
              against until it exists, and activation must not ride along on an
              unrelated Save. See PartnerLocationsFieldset's header. */}
          {mode === 'edit' && partner ? (
            <PartnerLocationsFieldset
              partnerId={partner.id}
              isDriveCarsPartner={isDriveCarsPartner(partner)}
            />
          ) : null}

          {/* Included crews — WHO they may sell, after WHERE. Edit-only and
              self-saving for the same reasons the cities fieldset is; see its
              header. */}
          {mode === 'edit' && partner ? (
            <PartnerVendorsFieldset
              partnerId={partner.id}
              isDriveCarsPartner={isDriveCarsPartner(partner)}
              commercialModel={partner.commercial_model ?? 'booking_fee'}
            />
          ) : null}

          {/* Enabled components */}
          <fieldset>
            <legend className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              Enabled components
            </legend>
            <p className="mt-1 text-[11.5px] text-[color:var(--bo-mute)]">
              Which widgets may be embedded on their page. Not what they may
              sell — that is the products list above.
            </p>
            <div className="mt-1.5 flex flex-wrap gap-3">
              {ALL_COMPONENTS.map((comp) => (
                <label key={comp} className="flex items-center gap-1.5 text-[13px]">
                  <input
                    type="checkbox"
                    checked={(draft.enabled_components ?? []).includes(comp)}
                    onChange={() => toggleComponent(comp)}
                  />
                  {/* Displayed capitalised, stored lowercase. The VALUE has to
                      stay lowercase — the widget loader's tag map is keyed
                      `drivecars-search`, and a capitalised tag is one it never
                      injects a bundle for. Only the label is cosmetic. */}
                  <span className="capitalize">{comp}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Authentication — WHOSE users these are. A third axis again, and
              deliberately its own fieldset for the same reason products and
              components are two: this one is about the person clicking Book,
              not about what may be sold to them or which widget shows it. */}
          <fieldset>
            <legend className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              Authentication
            </legend>
            <p className="mt-1 text-[11.5px] text-[color:var(--bo-mute)]">
              Who the shopper proves they are to. Partner auth is for partners
              whose users are already signed in on their own site.
            </p>
            <label className="mt-1.5 block">
              <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                Auth provider
              </span>
              <select
                aria-label="Auth provider"
                value={authState.auth_provider}
                onChange={(e) => setAuthProvider(e.target.value as PartnerAuthProvider)}
                className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
              >
                {PARTNER_AUTH_PROVIDERS.map((provider) => (
                  <option key={provider} value={provider}>
                    {AUTH_PROVIDER_LABEL[provider]}
                  </option>
                ))}
              </select>
            </label>

            {/* The URL and secret exist only in service of partner auth, so
                they are hidden on the DriveCars path rather than shown
                disabled: a visible-but-inert credential field invites ops to
                fill it in and wonder why nothing happened. */}
            {authState.auth_provider === 'partner' ? (
              <>
                <label className="mt-2.5 block">
                  <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                    Introspection URL <span className="text-[#ef4444]">*</span>
                  </span>
                  <input
                    type="url"
                    value={draft.auth_introspection_url ?? ''}
                    onChange={(e) =>
                      set(
                        'auth_introspection_url',
                        // '' → null, matching every other clearable field in
                        // this editor and what resolveAuthProviderUpdate
                        // treats as absent anyway.
                        e.target.value === '' ? null : e.target.value,
                      )
                    }
                    placeholder="https://auth.partner.example/introspect"
                    className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 font-mono text-[12.5px] outline-none focus:border-[color:var(--bo-accent)]"
                  />
                  <span className="mt-0.5 block text-[11px] text-[color:var(--bo-mute)]">
                    We POST their end-user’s token here, server-side — it does
                    not need to be reachable from a browser. Must be public
                    HTTPS; private and link-local addresses are refused on save.
                  </span>
                </label>

                <div className="mt-2.5">
                  <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                    Introspection bearer
                  </span>
                  {/* Three positions, because the API has three cases and only
                      an explicit control can express the third. See
                      secretForPayload — an empty box must never mean "clear". */}
                  {secretIntent.kind === 'keep' ? (
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-[12.5px] text-[color:var(--bo-ink)]">
                        {partner?.has_auth_introspection_secret
                          ? 'A secret is set.'
                          : 'No secret set.'}
                      </span>
                      <button
                        type="button"
                        onClick={() => setSecretIntent({ kind: 'replace', value: '' })}
                        className="inline-flex h-8 items-center rounded-lg border border-[color:var(--bo-line)] px-2.5 text-[12px] font-medium text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]"
                      >
                        {partner?.has_auth_introspection_secret ? 'Replace' : 'Set secret'}
                      </button>
                      {partner?.has_auth_introspection_secret ? (
                        <button
                          type="button"
                          onClick={() => setSecretIntent({ kind: 'clear' })}
                          className="inline-flex h-8 items-center rounded-lg border border-[color:var(--bo-line)] px-2.5 text-[12px] font-medium text-[#9f1239] hover:border-[#fecaca]"
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  {secretIntent.kind === 'replace' ? (
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="password"
                        aria-label="Introspection bearer"
                        autoComplete="new-password"
                        value={secretIntent.value}
                        onChange={(e) =>
                          setSecretIntent({ kind: 'replace', value: e.target.value })
                        }
                        placeholder="Paste the bearer we present"
                        className="h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 font-mono text-[12.5px] outline-none focus:border-[color:var(--bo-accent)]"
                      />
                      <button
                        type="button"
                        onClick={() => setSecretIntent({ kind: 'keep' })}
                        className="inline-flex h-8 shrink-0 items-center rounded-lg border border-[color:var(--bo-line)] px-2.5 text-[12px] font-medium text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : null}

                  {secretIntent.kind === 'clear' ? (
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-[12.5px] text-[#9f1239]">
                        The stored secret will be removed on save.
                      </span>
                      <button
                        type="button"
                        onClick={() => setSecretIntent({ kind: 'keep' })}
                        className="inline-flex h-8 items-center rounded-lg border border-[color:var(--bo-line)] px-2.5 text-[12px] font-medium text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]"
                      >
                        Keep it
                      </button>
                    </div>
                  ) : null}

                  <span className="mt-0.5 block text-[11px] text-[color:var(--bo-mute)]">
                    Optional — omit it if their endpoint needs no authorization.
                    Write-only: it is never shown again after saving.
                  </span>
                </div>
              </>
            ) : null}
          </fieldset>

          {/* Advanced (collapsed) */}
          <div className="rounded-lg border border-[color:var(--bo-line)]">
            <button
              type="button"
              className="flex w-full items-center justify-between px-3 py-2 text-[12px] font-semibold text-[color:var(--bo-mute)]"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              <span>Advanced overrides</span>
              <span>{showAdvanced ? '▲' : '▼'}</span>
            </button>
            {showAdvanced && (
              <div className="space-y-3 border-t border-[color:var(--bo-line)] px-3 pb-3 pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                      Platform % override
                    </span>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      value={draft.platform_pct_override ?? ''}
                      onChange={(e) =>
                        set(
                          'platform_pct_override',
                          e.target.value === '' ? null : Number(e.target.value),
                        )
                      }
                      placeholder="optional"
                      className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                      Pickup buffer override
                    </span>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={draft.pickup_buffer_override ?? ''}
                      onChange={(e) =>
                        set(
                          'pickup_buffer_override',
                          e.target.value === '' ? null : Number(e.target.value),
                        )
                      }
                      placeholder="optional"
                      className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                    Theme overrides (JSON)
                  </span>
                  <textarea
                    rows={5}
                    value={themeJson}
                    onChange={(e) => {
                      setThemeJson(e.target.value);
                      setThemeJsonError('');
                    }}
                    className="mt-1 w-full rounded-lg border border-[color:var(--bo-line)] px-3 py-2 font-mono text-[12px] outline-none focus:border-[color:var(--bo-accent)]"
                  />
                  {themeJsonError && (
                    <span className="mt-0.5 block text-[11px] text-[#ef4444]">{themeJsonError}</span>
                  )}
                </label>
              </div>
            )}
          </div>

          {/* Deep-link builder (edit mode only) */}
          {mode === 'edit' && partner && <DeepLinkBuilder slug={partner.slug} />}
          {mode === 'edit' && partner && <PartnerInsights partner={partner} />}
        </div>;

          if (mode !== 'edit' || !partner) return detailsContent;

          const tabs: DetailTab[] = [
            { id: 'details', label: 'Details', content: detailsContent },
            {
              id: 'credentials',
              label: 'Credentials',
              content: (
                <PartnerCredentialsPanel
                  partnerId={partner.id}
                  onSecretRevealed={onCredentialSecretRevealed}
                />
              ),
            },
            // No Team tab for DriveCars' own storefront row: it has no
            // partner console, so there is no team to list or invite.
            ...(partner.is_first_party
              ? []
              : [{ id: 'team', label: 'Team', content: <PartnerTeamPanel partnerId={partner.id} /> }]),
            // No Invoices tab for a DriveCars row either: it is the platform
            // selling to itself and the API refuses to cut it an invoice (409
            // first_party_no_invoice), so the tab would be a Generate button
            // that can only ever fail.
            //
            // Gated on isDriveCarsPartner — the FLAG OR THE SLUG, the same
            // pair the API's guard uses (see FIRST_PARTY_SLUGS above). A row
            // whose `is_first_party` was never backfilled is still ours, and
            // testing the flag alone would hand it the failing button.
            ...(isDriveCarsPartner(partner)
              ? []
              : [
                  {
                    id: 'invoices',
                    label: 'Invoices',
                    content: <PartnerInvoicesPanel partnerId={partner.id} />,
                  },
                ]),
          ];
          return <DetailTabs tabs={tabs} />;
        })()}

        {/* Action buttons */}
        <div className="mt-5 space-y-2">
          {/* Edit-mode action row (status + rotate) */}
          {mode === 'edit' && partner && (
            <div className="flex flex-wrap items-center gap-2">
              {/* Rotate secret */}
              {!rotateConfirm ? (
                <button
                  type="button"
                  onClick={() => setRotateConfirm(true)}
                  disabled={statusBusy || rotateBusy}
                  className="inline-flex h-9 items-center rounded-lg border border-[#d97706] px-3 text-[12px] font-medium text-[#d97706] hover:bg-[#fffbeb] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Rotate secret
                </button>
              ) : (
                <div className="flex items-center gap-1.5 rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2">
                  <span className="text-[11.5px] text-[#9f1239]">
                    This will invalidate the current secret. Existing sessions remain valid until expiry, but no new sessions can be minted with the old secret. Continue?
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setRotateConfirm(false);
                      onRotateSecret();
                    }}
                    disabled={rotateBusy}
                    className="ml-2 inline-flex h-7 items-center rounded px-2 text-[11.5px] font-semibold text-[#9f1239] underline hover:no-underline"
                  >
                    {rotateBusy ? 'Rotating…' : 'Yes, rotate'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRotateConfirm(false)}
                    className="inline-flex h-7 items-center rounded px-2 text-[11.5px] text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Status toggle */}
              {currentStatus === 'active' && (
                <button
                  type="button"
                  onClick={() => onSetStatus('suspend')}
                  disabled={statusBusy || rotateBusy}
                  className="inline-flex h-9 items-center rounded-lg border border-[#d97706] px-3 text-[12px] font-medium text-[#d97706] hover:bg-[#fffbeb] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {statusBusy ? 'Updating…' : 'Suspend'}
                </button>
              )}
              {currentStatus === 'suspended' && (
                <button
                  type="button"
                  onClick={() => onSetStatus('activate')}
                  disabled={statusBusy || rotateBusy}
                  className="inline-flex h-9 items-center rounded-lg border border-[#16a34a] px-3 text-[12px] font-medium text-[#16a34a] hover:bg-[#f0fdf4] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {statusBusy ? 'Updating…' : 'Activate'}
                </button>
              )}
              {currentStatus !== 'revoked' && (
                <button
                  type="button"
                  onClick={() => onSetStatus('revoke')}
                  disabled={statusBusy || rotateBusy}
                  className="inline-flex h-9 items-center rounded-lg border border-[#ef4444] px-3 text-[12px] font-medium text-[#ef4444] hover:bg-[#fff1f2] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {statusBusy ? 'Updating…' : 'Revoke'}
                </button>
              )}
            </div>
          )}

          {/* Save / Cancel */}
          {/* The reason is rendered NEXT to the disabled Save, not only as a
              title attribute: a Save that is greyed out for a reason the
              operator has to hover to discover reads as a broken screen, and
              the field it is about (the URL) may be scrolled out of view. */}
          {authBlockReason ? (
            <p role="alert" className="text-[11.5px] text-[#9f1239]">
              {authBlockReason}
            </p>
          ) : null}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 items-center rounded-lg border border-[color:var(--bo-line)] px-4 text-[12.5px] font-medium text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={
                busy ||
                !draft.name.trim() ||
                (mode === 'create' && !draft.slug?.trim()) ||
                authBlockReason !== null
              }
              className="inline-flex h-10 items-center rounded-lg bg-[color:var(--bo-ink)] px-4 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────

export function Partners() {
  const queryClient = useQueryClient();
  const { page, limit, pagination } = usePager();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'partners-list', page, limit],
    queryFn: ({ signal }) => fetchAdminPartnersList({ page, limit }, signal),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'partners-list'] });
  };

  // Pending secret to display in SecretRevealedModal
  const [pendingSecret, setPendingSecret] = useState<{
    secret: string;
    slug: string;
  } | null>(null);

  // Editor state
  const [editorState, setEditorState] = useState<{
    mode: 'create' | 'edit';
    partner?: AdminPartner;
    draft: PartnerWriteInput;
  } | null>(null);

  const createMut = useMutation({
    mutationFn: createPartner,
    onSuccess: (res) => {
      invalidate();
      setEditorState(null);
      setPendingSecret({ secret: res.oneTimeSecret, slug: res.partner.slug });
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<PartnerWriteInput> }) =>
      updatePartner(id, data),
    onSuccess: () => {
      invalidate();
      setEditorState(null);
    },
  });

  const rotateMut = useMutation({
    mutationFn: (id: number) => rotatePartnerSecret(id),
    onSuccess: (res) => {
      invalidate();
      // keep editor open but show secret
      setPendingSecret({ secret: res.oneTimeSecret, slug: res.partner.slug });
    },
  });

  const statusMut = useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'suspend' | 'activate' | 'revoke' }) =>
      setPartnerStatus(id, action),
    onSuccess: (res) => {
      invalidate();
      // Update the editor's partner reference with fresh data
      setEditorState((prev) =>
        prev ? { ...prev, partner: res.partner } : prev,
      );
    },
  });

  const mutationError =
    createMut.error ?? updateMut.error ?? rotateMut.error ?? statusMut.error;

  // The SSRF check on the introspection URL is server-side ON PURPOSE — it
  // resolves DNS, which a browser cannot — so its refusal necessarily arrives
  // as a 422, and `422 Unprocessable Entity` alone tells ops nothing about
  // which of the ~dozen fields they touched was wrong. The API puts the actual
  // reason in the body; this lifts it out. Same idiom as the credentials
  // panel's mutationErrorMessage above.
  const mutationErrorMessage = (() => {
    if (!mutationError) return null;
    if (mutationError instanceof ApiError) {
      const body = mutationError.body as { error?: string; reason?: string } | undefined;
      if (body?.error === 'unsafe_introspection_url') {
        return `That introspection URL was refused: ${body.reason ?? 'it must be a public HTTPS address.'}`;
      }
      if (body?.error === 'introspection_url_required') {
        return 'Partner auth needs an introspection URL.';
      }
      if (body?.error === 'unknown_auth_provider') {
        return 'That auth provider is not one this build knows about.';
      }
    }
    return (mutationError as Error).message;
  })();

  // Server-paginated: `partners` is one page, `total` the full count.
  const partners = data?.partners ?? [];
  const total = data?.total ?? 0;

  const cols = buildColumns((p) => {
    setEditorState({
      mode: 'edit',
      partner: p,
      draft: {
        slug: p.slug,
        name: p.name,
        origin_allowlist: p.origin_allowlist,
        mor_mode: p.mor_mode,
        // `?? 'variable'` guards a row read back before 0225 (or by a stale
        // API build), same reasoning as auth_provider's guard below: a
        // missing booking_mode must render as the default, not as an empty
        // select ops can save.
        booking_mode: p.booking_mode ?? 'variable',
        commission_pct: p.commission_pct,
        commission_flat: p.commission_flat,
        platform_pct_override: p.platform_pct_override,
        pickup_buffer_override: p.pickup_buffer_override,
        // `?? 'booking_fee'` / `?? 'drivecars'` guard a row read back from an
        // API build that predates 0241, the same way booking_mode and
        // auth_provider below are guarded: a missing value must render as the
        // default, not as an unchecked set of radios ops can save. The four
        // rates carry `?? null` because null is a real state (no share rule),
        // not an absence to be filled in.
        commercial_model: p.commercial_model ?? 'booking_fee',
        fee_share_pct: p.fee_share_pct ?? null,
        markup_pct: p.markup_pct ?? null,
        markup_share_pct: p.markup_share_pct ?? null,
        supplier_commission_share_pct: p.supplier_commission_share_pct ?? null,
        // `?? mor_mode` is the fallback 0241's own backfill applied, and the
        // one `rowToPartner` applies server-side — so a row read through a
        // stale SELECT renders the claim it actually mints.
        sdk_mor: p.sdk_mor ?? (p.mor_mode === 'drivecars' ? 'drivecars' : 'partner'),
        billing_cycle_days: p.billing_cycle_days,
        // Left undefined (so never PATCHed, and the field hidden) when the row
        // came from an API build that predates 0237.
        billing_cycle_anchor: p.billing_cycle_anchor ?? undefined,
        theme_overrides: p.theme_overrides,
        enabled_components: p.enabled_components,
        // `?? ['rentals']` guards a row read back before 0215's backfill (or
        // by a stale API build); an empty list here would render both
        // checkboxes off and let ops save a state the DB CHECK refuses.
        enabled_products: p.enabled_products ?? ['rentals'],
        default_product: p.default_product ?? null,
        // `?? 'drivecars'` guards a row read back before 0219 (or by a stale
        // API build), same as enabled_products above: a missing provider must
        // render as the default, not as an empty select ops can save.
        auth_provider: p.auth_provider ?? 'drivecars',
        auth_introspection_url: p.auth_introspection_url ?? null,
        // auth_introspection_secret is NOT hydrated — there is nothing to
        // hydrate it from (the API returns only has_auth_introspection_secret)
        // and its absence from the draft is exactly what keeps an ordinary
        // Save from touching the stored bearer.
      },
    });
  });

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Integrations"
        title="Partners"
        sub={`${total.toLocaleString()} partner${total === 1 ? '' : 's'} registered.`}
        actions={
          <>
            {/* The transfer section of the developer docs is not public: the
                API issues a grant to any active admin, and the docs site lets
                that grant in for twelve hours. The tab is opened before the
                call so the browser treats it as the click's own navigation. */}
            <ToolBtn
              title="Open the restricted airport-transfer docs in a new tab"
              onClick={() => {
                const tab = window.open('about:blank', '_blank');
                void api<{ url: string }>({ path: '/docs/access', method: 'POST' })
                  .then(({ url }) => { if (tab) tab.location.href = url; })
                  .catch(() => tab?.close());
              }}
            >
              Transfer docs ↗
            </ToolBtn>
            <ToolBtn
              icon="plus"
              primary
              onClick={() =>
                setEditorState({ mode: 'create', draft: { ...EMPTY_DRAFT } })
              }
            >
              New partner
            </ToolBtn>
          </>
        }
      />

      {error ? (
        <div role="alert" className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          Couldn&apos;t load partners: {(error as Error).message}
        </div>
      ) : null}

      {mutationError ? (
        <div role="alert" className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          Action failed: {mutationErrorMessage}
        </div>
      ) : null}

      <DataTable
        columns={cols}
        rows={partners}
        emptyLabel={isLoading ? 'Loading…' : 'No partners yet.'}
        pagination={pagination(total)}
      />

      {editorState ? (
        <PartnerEditor
          mode={editorState.mode}
          partner={editorState.partner}
          draft={editorState.draft}
          busy={createMut.isPending || updateMut.isPending}
          rotateBusy={rotateMut.isPending}
          statusBusy={statusMut.isPending}
          onClose={() => setEditorState(null)}
          onSave={(payload) => {
            if (editorState.mode === 'create') {
              createMut.mutate(payload);
            } else if (editorState.partner) {
              updateMut.mutate({ id: editorState.partner.id, data: payload });
            }
          }}
          onRotateSecret={() => {
            if (editorState.partner) {
              rotateMut.mutate(editorState.partner.id);
            }
          }}
          onSetStatus={(action) => {
            if (editorState.partner) {
              statusMut.mutate({ id: editorState.partner.id, action });
            }
          }}
          onCredentialSecretRevealed={(secret) => {
            // Reuses the SAME SecretRevealedModal the widget secret create/
            // rotate flow shows above — not a second modal. The
            // integration-snippet copy inside that modal is written for the
            // widget secret, not an API credential, but the "shown exactly
            // once, copy it now" framing and the secret-reveal UX itself are
            // identical for both kinds of secret, which is the whole reason
            // to reuse it rather than fork a near-duplicate.
            if (editorState.partner) {
              setPendingSecret({ secret, slug: editorState.partner.slug });
            }
          }}
        />
      ) : null}

      {pendingSecret ? (
        <SecretRevealedModal
          secret={pendingSecret.secret}
          slug={pendingSecret.slug}
          onDismiss={() => setPendingSecret(null)}
        />
      ) : null}
    </div>
  );
}
